import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import { useRequestData, useLabels, usePrefs } from 'wsm-common-data';
import { formatter, getResizedImageUrl } from '../helpers';
import { DEFAULT_IMAGE_WIDTH } from '../constants';
import { CustomModal } from './CustomModal';

const VehicleCard = ({ useTray, onChangeIndex, currentStep }) => {
	const labels = useLabels();
	const navigate = useNavigate();
	const { modalText } = usePrefs();
	const { deviceType } = useRequestData();
	const { vehicleConfiguration, rootDir } = useSelector((state) => state);
	const { year, make, model, trimName, price, bodyStyle, image } =
		vehicleConfiguration;

	const placeHolderImgUrl =
		'/static/v8/global/images/bodystyle/160x87/sedan_ext_225.png';

	/* static links  populated via index 0:configuration  1:color steps */
	const baseEditURL = `/${rootDir}/${vehicleConfiguration.make}/${vehicleConfiguration.model}.htm?trim=${vehicleConfiguration.trimName}`;

	const steps = [
		{
			url: `/${rootDir}/${vehicleConfiguration.make}/${vehicleConfiguration.model}.htm?trim=${vehicleConfiguration.trimName}`,
			stepName: 'configuration',
			optionName: 'drivetrain'
		},
		{
			url: `${baseEditURL}&driveTrain=${vehicleConfiguration.options.drivetrain.value}`,
			stepName: 'color',
			optionName: 'paint'
		},
		{
			url: `${baseEditURL}&driveTrain=${vehicleConfiguration.options.drivetrain.value}&paint=${vehicleConfiguration.options.paint}`,
			stepName: 'packages',
			optionName: 'packages'
		}
	];

	const handleEditOption = (index) => {
		const { url } = steps[index];
		navigate(url);
		if (
			deviceType === 'MOBILE' &&
			typeof useTray === 'boolean' &&
			useTray === true
		) {
			onChangeIndex(0);
		}
	};

	const deviceSpecificClasses = () => {
		if (deviceType === 'DESKTOP') {
			return 'w-50 box-border rounded';
		} else {
			if (useTray) {
				return 'text-center';
			}
			return 'box-border border-radius-lg mx-3';
		}
	};
	const [isModalVisible, setIsModalVisible] = useState(false);

	const translatedDisclaimerTitle = labels.get('DISCLAIMER');
	const modalDisclaimerMessage = labels.get(modalText);

	const modalPrefs = {
		title: translatedDisclaimerTitle,
		modalContent: modalDisclaimerMessage,
		isOpen: isModalVisible,
		onClose: () => setIsModalVisible(false),
		onOpen: () => setIsModalVisible(true)
	};

	return (
		<div
			data-testid="vehicle-card"
			className={`vehicle-card box px-6 my-4 py-md-0 mb-md-6 mt-md-3 ${deviceSpecificClasses()}`}
		>
			<h1 className="line-height-reset">
				<span className="d-block h2 font-weight-bold">
					{year} {make}
				</span>
				<span className="font-weight-bold">
					{model} {trimName} {bodyStyle}
				</span>
			</h1>
			<p className="ddc-font-size-xsmall text-muted">
				{labels.get('AS_CONFIGURED')}{' '}
				<i
					className="ddc-icon ddc-icon-info-outline"
					aria-hidden="true"
					onClick={() => setIsModalVisible(true)}
				/>
				<CustomModal {...modalPrefs} />
			</p>
			<p className="display-4">{formatter.format(price)}</p>
			<img
				className="center-block"
				src={`${
					image
						? getResizedImageUrl(image, DEFAULT_IMAGE_WIDTH)
						: placeHolderImgUrl
				}`}
				alt={`${year} ${make} ${model}`}
			/>
			<Table striped>
				<tbody>
					{Object.entries(vehicleConfiguration.options).map(
						(option) => {
							const [optionName, optionVal] = option;
							const optionStepObj = steps.find(
								(step) => step.optionName === optionName
							);
							const showEditButton =
								optionName !== 'packages' &&
								optionVal.modified &&
								optionStepObj?.stepName !== undefined &&
								optionStepObj?.stepName !== currentStep &&
								currentStep !== 'confirm';
							const showEditButtonForPackages =
								optionName === 'packages' &&
								optionStepObj?.stepName !== undefined &&
								optionStepObj?.stepName !== currentStep &&
								currentStep !== 'packages' &&
								currentStep !== 'confirm';
							const optionIndex = steps.findIndex(
								(step) => step.optionName === optionName
							);
							return optionVal.selections?.length
								? optionVal.selections.map((selection) => {
										return (
											<tr
												key={`${optionName}-${selection.description}`}
											>
												<td className="align-left border-0 table-edit">
													{showEditButtonForPackages ? (
														<button
															type="button"
															className="btn-unstyled p-0"
															data-testid="edit-option-button"
															aria-label="Edit option"
															onClick={() =>
																handleEditOption(
																	optionIndex
																)
															}
														>
															<i
																className="ncc-edit-icon"
																data-testid="edit-icon"
															/>
														</button>
													) : null}
												</td>

												<td className="align-left border-0 table-name">
													{selection.description}
												</td>
												<td className="align-right border-0 table-price">
													{selection.msrp
														? formatter.format(
																Math.abs(
																	selection.msrp
																)
														  )
														: '—'}
												</td>
											</tr>
										);
								  })
								: optionVal.valueLabel && (
										/* eslint-disable react/jsx-indent */
										<tr key={optionName}>
											<td className="align-left border-0 table-edit">
												{showEditButton ? (
													<button
														type="button"
														className="btn-unstyled p-0"
														data-testid="edit-option-button"
														aria-label="Edit option"
														onClick={() =>
															handleEditOption(
																optionIndex
															)
														}
													>
														<i
															className="ncc-edit-icon"
															data-testid="edit-icon"
														/>
													</button>
												) : null}
											</td>
											<td className="align-left border-0 table-name">
												{optionVal.keyLabel}
											</td>

											<td className="align-right border-0 table-price">
												{optionVal.valueLabel}
											</td>
										</tr>
										/* eslint-enable react/jsx-indent */
								  );
						}
					)}
				</tbody>
			</Table>
			{useTray && (
				<button
					className="btn btn-block btn-primary"
					onClick={() => onChangeIndex(0)}
					type="button"
				>
					{labels.get('CONTINUE')}
				</button>
			)}
		</div>
	);
};

VehicleCard.propTypes = {
	useTray: PropTypes.bool,
	onChangeIndex: PropTypes.func,
	currentStep: PropTypes.string
};

export default VehicleCard;
